import React, { useEffect, useState } from 'react';
import { Calculator, Trash2 } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Separator } from '../../components/ui/separator';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { fetchRoofPricingConfig, fetchPricingConfig, updatePricingConfig, initStarterPricingConfig } from '../../services/pricingService';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

const initialRoofErrors = {
  materials: {
    asphalt: '',
    cedar: '',
    stone: '',
  },
  pitchMultipliers: {},
  stageAddons: {
    stage1: '',
    stage2: '',
    stage3: '',
  },
  storyAdjustments: {
    twoStoryAdd: '',
    droneMulti: '',
  },
  taxRate: '',
};

const initialHouseWashErrors = {
  baseRatePerSqFt: '',
  minCharge: '',
  storyMultiplier: {
    '1': '',
    '2': '',
    '3': '',
  },
  taxRate: '',
};

const initialSidingErrors = {
  baseRatePerSqFt: '',
  minCharge: '',
  materials: {
    vinyl: {
      baseRatePerSqFt: '',
      minCharge: '',
    },
    hardie: {
      baseRatePerSqFt: '',
      minCharge: '',
    },
    wood: {
      baseRatePerSqFt: '',
      minCharge: '',
    },
  },
  storyMultiplier: {
    '1': '',
    '2': '',
    '3': '',
  },
  taxRate: '',
};

const initialGutterErrors = {
  baseRatePerFoot: '',
  storyMultiplier: {
    '1': '',
    '2': '',
    '3': '',
  },
  minCharge: '',
  taxRate: '',
};

const initialDrivewayErrors = {
  baseRatePerSqFt: '',
  minCharge: '',
  taxRate: '',
};

const initialDeckErrors = {
  baseRatePerSqFt: {
    wood: '',
    composite: '',
  },
  minCharge: '',
  taxRate: '',
};

const initialFenceErrors = {
  baseRatePerFoot: {
    '4ft': '',
    '6ft': '',
    '8ft': '',
  },
  minCharge: '',
  taxRate: '',
};

const initialErrors = {
  roof: initialRoofErrors,
  houseWash: initialHouseWashErrors,
  siding: initialSidingErrors,
  gutter: initialGutterErrors,
  driveway: initialDrivewayErrors,
  deck: initialDeckErrors,
  fence: initialFenceErrors,
};

const requiredPitches = [
  '1/2',
  '2/12',
  '3/12',
  '4/12',
  '5/12',
  '6/12',
  '7/12',
  '8/12',
  '9/12',
  '10/12',
  '11/12',
  '12/12',
];

import { useAuth } from '../../context/AuthContext';

const PricingSettings = () => {
  const { isAuthLoading, user } = useAuth();

  const [formState, setFormState] = useState({
    roof: null,
    houseWash: {
      baseRatePerSqFt: '',
      minCharge: '',
      storyMultiplier: { '1': '', '2': '', '3': '' },
      taxRate: '',
    },
    siding: {
      baseRatePerSqFt: '',
      minCharge: '',
      materials: {
        vinyl:  { baseRatePerSqFt: '', minCharge: '' },
        hardie: { baseRatePerSqFt: '', minCharge: '' },
        wood:   { baseRatePerSqFt: '', minCharge: '' },
      },
      storyMultiplier: { '1': '', '2': '', '3': '' },
      taxRate: '',
    },
    gutter: {
      baseRatePerFoot: '',
      storyMultiplier: { '1': '', '2': '', '3': '' },
      minCharge: '',
      taxRate: '',
    },
    driveway: { baseRatePerSqFt: '', minCharge: '', taxRate: '' },
    deck: {
      baseRatePerSqFt: { wood: '', composite: '' },
      minCharge: '',
      taxRate: '',
    },
    fence: {
      baseRatePerFoot: { '4ft': '', '6ft': '', '8ft': '' },
      minCharge: '',
      taxRate: '',
    },
    pest_control: {
      bands: [
        { id: 'b0', minSqFt: 0,    maxSqFt: 1500, basePrice: '' },
        { id: 'b1', minSqFt: 1501, maxSqFt: 3000, basePrice: '' },
        { id: 'b2', minSqFt: 3001, maxSqFt: null,  basePrice: '' },
      ],
      materialCostDefault: '',
      marginPct: '',
      taxRate: '',
      defaultFrequency: 'one-time',
    },
    lawn_care: {
      bands: [
        { id: 'b0', minSqFt: 0,    maxSqFt: 1500, basePrice: '' },
        { id: 'b1', minSqFt: 1501, maxSqFt: 3000, basePrice: '' },
        { id: 'b2', minSqFt: 3001, maxSqFt: null,  basePrice: '' },
      ],
      materialCostDefault: '',
      marginPct: '',
      taxRate: '',
      defaultFrequency: 'one-time',
    },
  });

  const [errors, setErrors] = useState(initialErrors);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isSavingSiding, setIsSavingSiding]     = useState(false);
  const [isSavingGutter, setIsSavingGutter]     = useState(false);
  const [isSavingDriveway, setIsSavingDriveway] = useState(false);
  const [isSavingDeck, setIsSavingDeck]         = useState(false);
  const [isSavingFence, setIsSavingFence]       = useState(false);
  const [isSavingHouseWash, setIsSavingHouseWash] = useState(false);
  const [isSavingPest, setIsSavingPest]         = useState(false);
  const [isSavingLawn, setIsSavingLawn]         = useState(false);
  const [isLoadingStarter, setIsLoadingStarter] = useState(false);

  // Option B — explicitly copy starter/template values into this company's
  // own pricing docs for any service not yet configured. Non-destructive:
  // already-saved services are left untouched. Does NOT change tax/billing.
  const handleLoadStarterValues = async () => {
    setIsLoadingStarter(true);
    const services = ['roof', 'houseWash', 'siding', 'gutter', 'driveway', 'deck', 'fence', 'pest_control', 'lawn_care'];
    try {
      await Promise.all(services.map((s) => initStarterPricingConfig(s).catch(() => null)));
      toast.success('Starter values loaded into your pricing settings. Review and Save each service.');
      await loadConfig();
    } catch {
      toast.error('Failed to load starter values.');
    } finally {
      setIsLoadingStarter(false);
    }
  };

  // Derived error flags for new services
  const hasSidingErrors = React.useMemo(() => {
    const s = errors.siding;
    if (s.taxRate || s.baseRatePerSqFt || s.minCharge) return true;
    const storyErrs = Object.values(s.storyMultiplier || {});
    if (storyErrs.some((e) => e)) return true;
    
    // Check material errors
    const materials = s.materials || {};
    for (const materialKey of ['vinyl', 'hardie', 'wood']) {
      const material = materials[materialKey] || {};
      if (material.baseRatePerSqFt || material.minCharge) return true;
    }
    
    return false;
  }, [errors.siding]);

  const hasGutterErrors = React.useMemo(() => {
    const g = errors.gutter;
    if (g.taxRate || g.baseRatePerFoot || g.minCharge) return true;
    const storyErrs = Object.values(g.storyMultiplier || {});
    if (storyErrs.some((e) => e)) return true;
    return false;
  }, [errors.gutter]);

  const hasDrivewayErrors = React.useMemo(() => {
    const d = errors.driveway;
    if (d.taxRate || d.baseRatePerSqFt || d.minCharge) return true;
    return false;
  }, [errors.driveway]);

  const hasDeckErrors = React.useMemo(() => {
    const d = errors.deck;
    if (d.taxRate || d.minCharge) return true;
    const base = d.baseRatePerSqFt || {};
    if (base.wood || base.composite) return true;
    return false;
  }, [errors.deck]);

  const hasFenceErrors = React.useMemo(() => {
    const f = errors.fence;
    if (f.taxRate || f.minCharge) return true;
    const base = f.baseRatePerFoot || {};
    if (base['4ft'] || base['6ft'] || base['8ft']) return true;
    return false;
  }, [errors.fence]);

  const hasRoofErrors = React.useMemo(() => {
    if (!formState.roof) return true;
    const roofErrors = errors.roof;
    if (roofErrors.taxRate) return true;

    const materialErrors = Object.values(roofErrors.materials || {});
    if (materialErrors.some((e) => e)) return true;

    const pitchErrors = Object.values(roofErrors.pitchMultipliers || {});
    if (pitchErrors.some((e) => e)) return true;

    const stageErrors = Object.values(roofErrors.stageAddons || {});
    if (stageErrors.some((e) => e)) return true;

    const storyErrors = Object.values(roofErrors.storyAdjustments || {});
    if (storyErrors.some((e) => e)) return true;

    return false;
  }, [errors.roof, formState.roof]);

  const hasHouseWashErrors = React.useMemo(() => {
    const hw = errors.houseWash;
    if (hw.taxRate) return true;
    if (hw.baseRatePerSqFt || hw.minCharge) return true;
    const storyErrs = Object.values(hw.storyMultiplier || {});
    if (storyErrs.some((e) => e)) return true;
    return false;
  }, [errors.houseWash]);

  // ----------------------------
  // Load config on mount
  // ----------------------------
  const loadConfig = async () => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const [roofResp, houseWashResp, sidingResp, gutterResp, drivewayResp, deckResp, fenceResp, pestResp, lawnResp] = await Promise.all([
        fetchRoofPricingConfig(),
        fetchPricingConfig('houseWash'),
        fetchPricingConfig('siding'),
        fetchPricingConfig('gutter'),
        fetchPricingConfig('driveway'),
        fetchPricingConfig('deck'),
        fetchPricingConfig('fence'),
        fetchPricingConfig('pest_control'),
        fetchPricingConfig('lawn_care'),
      ]);

      if (!roofResp || !roofResp.success || !roofResp.config) {
        toast.error('Failed to load roof pricing settings.');
        setLoadFailed(true);
        return;
      }

      const roofCfg = roofResp.config;
      const normalizedRoof = {
        materials: roofCfg.materials || {},
        pitchMultipliers: roofCfg.pitchMultipliers || {},
        stageAddons: roofCfg.stageAddons || {},
        storyAdjustments: roofCfg.storyAdjustments || {},
        taxRate: roofCfg.taxRate ?? 6.35,
      };

      let nextHouseWash = {
        baseRatePerSqFt: '',
        minCharge: '',
        storyMultiplier: {
          '1': '',
          '2': '',
          '3': '',
        },
        taxRate: '',
      };

      if (houseWashResp && houseWashResp.success && houseWashResp.config) {
        const hwCfg = houseWashResp.config;
        nextHouseWash = {
          baseRatePerSqFt: hwCfg.baseRatePerSqFt ?? '',
          minCharge: hwCfg.minCharge ?? '',
          storyMultiplier: {
            '1': hwCfg.storyMultiplier?.['1'] ?? '',
            '2': hwCfg.storyMultiplier?.['2'] ?? '',
            '3': hwCfg.storyMultiplier?.['3'] ?? '',
          },
          taxRate: hwCfg.taxRate ?? '',
        };
      }

      let nextSiding = {
        baseRatePerSqFt: '',
        minCharge: '',
        materials: {
          vinyl: {
            baseRatePerSqFt: '',
            minCharge: '',
          },
          hardie: {
            baseRatePerSqFt: '',
            minCharge: '',
          },
          wood: {
            baseRatePerSqFt: '',
            minCharge: '',
          },
        },
        storyMultiplier: {
          '1': '',
          '2': '',
          '3': '',
        },
        taxRate: '',
      };

      if (sidingResp && sidingResp.success && sidingResp.config) {
        const sCfg = sidingResp.config;
        nextSiding = {
          baseRatePerSqFt: sCfg.baseRatePerSqFt ?? '',
          minCharge: sCfg.minCharge ?? '',
          materials: {
            vinyl: {
              baseRatePerSqFt: sCfg.materials?.vinyl?.baseRatePerSqFt ?? '',
              minCharge: sCfg.materials?.vinyl?.minCharge ?? '',
            },
            hardie: {
              baseRatePerSqFt: sCfg.materials?.hardie?.baseRatePerSqFt ?? '',
              minCharge: sCfg.materials?.hardie?.minCharge ?? '',
            },
            wood: {
              baseRatePerSqFt: sCfg.materials?.wood?.baseRatePerSqFt ?? '',
              minCharge: sCfg.materials?.wood?.minCharge ?? '',
            },
          },
          storyMultiplier: {
            '1': sCfg.storyMultiplier?.['1'] ?? '',
            '2': sCfg.storyMultiplier?.['2'] ?? '',
            '3': sCfg.storyMultiplier?.['3'] ?? '',
          },
          taxRate: sCfg.taxRate ?? '',
        };
      }

      let nextGutter = {
        baseRatePerFoot: '',
        storyMultiplier: {
          '1': '',
          '2': '',
          '3': '',
        },
        minCharge: '',
        taxRate: '',
      };

      if (gutterResp && gutterResp.success && gutterResp.config) {
        const gCfg = gutterResp.config;
        nextGutter = {
          baseRatePerFoot: gCfg.baseRatePerFoot ?? '',
          storyMultiplier: {
            '1': gCfg.storyMultiplier?.['1'] ?? '',
            '2': gCfg.storyMultiplier?.['2'] ?? '',
            '3': gCfg.storyMultiplier?.['3'] ?? '',
          },
          minCharge: gCfg.minCharge ?? '',
          taxRate: gCfg.taxRate ?? '',
        };
      }

      let nextDriveway = {
        baseRatePerSqFt: '',
        minCharge: '',
        taxRate: '',
      };

      if (drivewayResp && drivewayResp.success && drivewayResp.config) {
        const dCfg = drivewayResp.config;
        nextDriveway = {
          baseRatePerSqFt: dCfg.baseRatePerSqFt ?? '',
          minCharge: dCfg.minCharge ?? '',
          taxRate: dCfg.taxRate ?? '',
        };
      }

      let nextDeck = {
        baseRatePerSqFt: {
          wood: '',
          composite: '',
        },
        minCharge: '',
        taxRate: '',
      };

      if (deckResp && deckResp.success && deckResp.config) {
        const dCfg = deckResp.config;
        nextDeck = {
          baseRatePerSqFt: {
            wood: dCfg.baseRatePerSqFt?.wood ?? '',
            composite: dCfg.baseRatePerSqFt?.composite ?? '',
          },
          minCharge: dCfg.minCharge ?? '',
          taxRate: dCfg.taxRate ?? '',
        };
      }

      let nextFence = {
        baseRatePerFoot: {
          '4ft': '',
          '6ft': '',
          '8ft': '',
        },
        minCharge: '',
        taxRate: '',
      };

      if (fenceResp && fenceResp.success && fenceResp.config) {
        const fCfg = fenceResp.config;
        nextFence = {
          baseRatePerFoot: {
            '4ft': fCfg.baseRatePerFoot?.['4ft'] ?? '',
            '6ft': fCfg.baseRatePerFoot?.['6ft'] ?? '',
            '8ft': fCfg.baseRatePerFoot?.['8ft'] ?? '',
          },
          minCharge: fCfg.minCharge ?? '',
          taxRate: fCfg.taxRate ?? '',
        };
      }

      const parseFlatRate = (resp) => {
        const defaultBands = [
          { id: 'b0', minSqFt: 0,    maxSqFt: 1500, basePrice: '' },
          { id: 'b1', minSqFt: 1501, maxSqFt: 3000, basePrice: '' },
          { id: 'b2', minSqFt: 3001, maxSqFt: null,  basePrice: '' },
        ];
        const empty = {
          bands: defaultBands,
          materialCostDefault: '',
          marginPct: '',
          taxRate: '',
          defaultFrequency: 'one-time',
        };
        if (!resp?.success || !resp.config) return empty;
        const c = resp.config;

        let bands;
        if (Array.isArray(c.bands) && c.bands.length > 0) {
          // New format — use directly
          bands = c.bands.map((b) => ({
            id:        b.id ?? `b_${Math.random()}`,
            minSqFt:   b.minSqFt  ?? 0,
            maxSqFt:   b.maxSqFt  ?? null,
            basePrice: b.basePrice ?? '',
          }));
        } else if (c.tiers) {
          // Legacy migration: small/medium/large → bands
          bands = [
            { id: 'b0', minSqFt: 0,    maxSqFt: 1500, basePrice: c.tiers.small?.basePrice  ?? '' },
            { id: 'b1', minSqFt: 1501, maxSqFt: 3000, basePrice: c.tiers.medium?.basePrice ?? '' },
            { id: 'b2', minSqFt: 3001, maxSqFt: null,  basePrice: c.tiers.large?.basePrice  ?? '' },
          ];
        } else {
          bands = defaultBands;
        }

        return {
          bands,
          materialCostDefault: c.materialCostDefault ?? '',
          marginPct:           c.marginPct           ?? '',
          taxRate:             c.taxRate             ?? '',
          defaultFrequency:    c.defaultFrequency    ?? 'one-time',
        };
      };

      setFormState({
        roof: normalizedRoof,
        houseWash: nextHouseWash,
        siding: nextSiding,
        gutter: nextGutter,
        driveway: nextDriveway,
        deck: nextDeck,
        fence: nextFence,
        pest_control: parseFlatRate(pestResp),
        lawn_care:    parseFlatRate(lawnResp),
      });

      setErrors(initialErrors);
    } catch (e) {
      console.error('Failed to load pricing settings', e);
      // For non-roof services, fail silently and keep defaults.
    } finally {
      setIsLoading(false);
    }
  };

  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, []);

  // Error helper functions
  const updateSidingErrorState = (keyPath, message) => {
    setErrors((prev) => {
      const next = { ...prev.siding };
      if (keyPath === 'taxRate' || keyPath === 'baseRatePerSqFt' || keyPath === 'minCharge') {
        next[keyPath] = message;
      } else if (keyPath.startsWith('storyMultiplier.')) {
        const k = keyPath.split('.')[1];
        next.storyMultiplier = {
          ...(next.storyMultiplier || {}),
          [k]: message,
        };
      }
      return { ...prev, siding: next };
    });
  };

  const updateSimpleServiceErrorState = (serviceKey, field, message) => {
    setErrors((prev) => ({
      ...prev,
      [serviceKey]: {
        ...prev[serviceKey],
        [field]: message,
      },
    }));
  };

  // Validation functions
  const validateSidingBaseRate = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      updateSidingErrorState('baseRatePerSqFt', 'Must be a number ≥ 0');
    } else {
      updateSidingErrorState('baseRatePerSqFt', '');
    }
  };

  const validateSidingMinCharge = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      updateSidingErrorState('minCharge', 'Must be a number ≥ 0');
    } else {
      updateSidingErrorState('minCharge', '');
    }
  };

  const validateSidingStoryMultiplier = (storyKey, value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) {
      updateSidingErrorState(`storyMultiplier.${storyKey}`, 'Must be a valid number');
    } else {
      updateSidingErrorState(`storyMultiplier.${storyKey}`, '');
    }
  };

  const validateSidingTaxRate = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      updateSidingErrorState('taxRate', 'Must be a number ≥ 0');
    } else {
      updateSidingErrorState('taxRate', '');
    }
  };

  const validateSimpleServiceBaseRate = (serviceKey, field, value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      updateSimpleServiceErrorState(serviceKey, field, 'Must be a number ≥ 0');
    } else {
      updateSimpleServiceErrorState(serviceKey, field, '');
    }
  };

  const validateSimpleServiceMinCharge = (serviceKey, value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      updateSimpleServiceErrorState(serviceKey, 'minCharge', 'Must be a number ≥ 0');
    } else {
      updateSimpleServiceErrorState(serviceKey, 'minCharge', '');
    }
  };

  const validateSimpleServiceTaxRate = (serviceKey, value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      updateSimpleServiceErrorState(serviceKey, 'taxRate', 'Must be a number ≥ 0');
    } else {
      updateSimpleServiceErrorState(serviceKey, 'taxRate', '');
    }
  };

  if (isAuthLoading || !user) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading pricing settings...</p>
      </div>
    );
  }

  // Handler functions
  const handleSidingBaseRateChange = (value) => {
    setFormState((prev) => ({
      ...prev,
      siding: {
        ...prev.siding,
        baseRatePerSqFt: value,
      },
    }));
    validateSidingBaseRate(value);
  };

  const handleSidingMinChargeChange = (value) => {
    setFormState((prev) => ({
      ...prev,
      siding: {
        ...prev.siding,
        minCharge: value,
      },
    }));
    validateSidingMinCharge(value);
  };

  const handleSidingStoryMultiplierChange = (storyKey, value) => {
    setFormState((prev) => ({
      ...prev,
      siding: {
        ...prev.siding,
        storyMultiplier: {
          ...(prev.siding?.storyMultiplier || {}),
          [storyKey]: value,
        },
      },
    }));
    validateSidingStoryMultiplier(storyKey, value);
  };

  const handleSidingTaxRateChange = (value) => {
    setFormState((prev) => ({
      ...prev,
      siding: {
        ...prev.siding,
        taxRate: value,
      },
    }));
    validateSidingTaxRate(value);
  };

  const handleSidingMaterialBaseRateChange = (materialKey, value) => {
    setFormState((prev) => ({
      ...prev,
      siding: {
        ...prev.siding,
        materials: {
          ...(prev.siding?.materials || {}),
          [materialKey]: {
            ...(prev.siding?.materials?.[materialKey] || {}),
            baseRatePerSqFt: value,
          },
        },
      },
    }));
    // Validate the material base rate
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      setErrors((prev) => ({
        ...prev,
        siding: {
          ...prev.siding,
          materials: {
            ...(prev.siding?.materials || {}),
            [materialKey]: {
              ...(prev.siding?.materials?.[materialKey] || {}),
              baseRatePerSqFt: 'Must be a number ≥ 0',
            },
          },
        },
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        siding: {
          ...prev.siding,
          materials: {
            ...(prev.siding?.materials || {}),
            [materialKey]: {
              ...(prev.siding?.materials?.[materialKey] || {}),
              baseRatePerSqFt: '',
            },
          },
        },
      }));
    }
  };

  const handleSidingMaterialMinChargeChange = (materialKey, value) => {
    setFormState((prev) => ({
      ...prev,
      siding: {
        ...prev.siding,
        materials: {
          ...(prev.siding?.materials || {}),
          [materialKey]: {
            ...(prev.siding?.materials?.[materialKey] || {}),
            minCharge: value,
          },
        },
      },
    }));
    // Validate the material min charge
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      setErrors((prev) => ({
        ...prev,
        siding: {
          ...prev.siding,
          materials: {
            ...(prev.siding?.materials || {}),
            [materialKey]: {
              ...(prev.siding?.materials?.[materialKey] || {}),
              minCharge: 'Must be a number ≥ 0',
            },
          },
        },
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        siding: {
          ...prev.siding,
          materials: {
            ...(prev.siding?.materials || {}),
            [materialKey]: {
              ...(prev.siding?.materials?.[materialKey] || {}),
              minCharge: '',
            },
          },
        },
      }));
    }
  };

  const handleSimpleServiceBaseRateChange = (serviceKey, field, value) => {
    setFormState((prev) => ({
      ...prev,
      [serviceKey]: {
        ...prev[serviceKey],
        [field]: value,
      },
    }));
    validateSimpleServiceBaseRate(serviceKey, field, value);
  };

  const handleFenceBaseRateChange = (heightKey, value) => {
    setFormState((prev) => ({
      ...prev,
      fence: {
        ...prev.fence,
        baseRatePerFoot: {
          ...(prev.fence?.baseRatePerFoot || {}),
          [heightKey]: value,
        },
      },
    }));
    const num = Number(value);
    setErrors((prev) => ({
      ...prev,
      fence: {
        ...prev.fence,
        baseRatePerFoot: {
          ...(prev.fence?.baseRatePerFoot || {}),
          [heightKey]: !Number.isFinite(num) || num < 0 ? 'Must be a number ≥ 0' : '',
        },
      },
    }));
  };

  const handleDeckBaseRateChange = (materialKey, value) => {
    setFormState((prev) => ({
      ...prev,
      deck: {
        ...prev.deck,
        baseRatePerSqFt: {
          ...(prev.deck?.baseRatePerSqFt || {}),
          [materialKey]: value,
        },
      },
    }));
    const num = Number(value);
    setErrors((prev) => ({
      ...prev,
      deck: {
        ...prev.deck,
        baseRatePerSqFt: {
          ...(prev.deck?.baseRatePerSqFt || {}),
          [materialKey]: !Number.isFinite(num) || num < 0 ? 'Must be a number ≥ 0' : '',
        },
      },
    }));
  };


  const handleSimpleServiceMinChargeChange = (serviceKey, value) => {
    setFormState((prev) => ({
      ...prev,
      [serviceKey]: {
        ...prev[serviceKey],
        minCharge: value,
      },
    }));
    validateSimpleServiceMinCharge(serviceKey, value);
  };

  const handleSimpleServiceTaxRateChange = (serviceKey, value) => {
    setFormState((prev) => ({
      ...prev,
      [serviceKey]: {
        ...prev[serviceKey],
        taxRate: value,
      },
    }));
    validateSimpleServiceTaxRate(serviceKey, value);
  };

  // ── Band validation ────────────────────────────────────────────────────
  const validateBands = (bands) => {
    if (!bands || bands.length === 0)
      return { valid: false, error: 'At least one pricing band is required.' };
    for (let i = 0; i < bands.length; i++) {
      const b = bands[i];
      if (!b.basePrice || parseFloat(b.basePrice) <= 0)
        return { valid: false, error: `Band ${i + 1}: price must be greater than $0.` };
      if (parseInt(b.minSqFt, 10) < 0)
        return { valid: false, error: `Band ${i + 1}: minimum sq ft cannot be negative.` };
      if (b.maxSqFt !== null && parseInt(b.maxSqFt, 10) <= parseInt(b.minSqFt, 10))
        return { valid: false, error: `Band ${i + 1}: upper bound must be greater than lower bound.` };
      if (b.maxSqFt === null && i < bands.length - 1)
        return { valid: false, error: 'Only the last band can be open-ended.' };
    }
    if (bands[bands.length - 1].maxSqFt !== null)
      return { valid: false, error: 'The last band must be open-ended (no upper limit).' };

    // Overlap check on sorted copy
    const sorted = [...bands].sort(
      (a, b) => parseInt(a.minSqFt, 10) - parseInt(b.minSqFt, 10)
    );
    for (let i = 0; i < sorted.length - 1; i++) {
      if (
        sorted[i].maxSqFt !== null &&
        parseInt(sorted[i].maxSqFt, 10) >= parseInt(sorted[i + 1].minSqFt, 10)
      ) {
        return { valid: false, error: `Bands ${i + 1} and ${i + 2} overlap — adjust the boundaries.` };
      }
    }
    return { valid: true };
  };

  // ── Band management helpers ─────────────────────────────────────────────
  const updateBandField = (serviceKey, bandId, field, rawValue) => {
    setFormState((prev) => ({
      ...prev,
      [serviceKey]: {
        ...prev[serviceKey],
        bands: prev[serviceKey].bands.map((b) =>
          b.id === bandId
            ? {
                ...b,
                [field]:
                  field === 'basePrice'
                    ? rawValue                                   // keep as string for input
                    : rawValue === '' ? '' : Number(rawValue),   // numeric for sqFt fields
              }
            : b
        ),
      },
    }));
  };

  const addBand = (serviceKey) => {
    setFormState((prev) => {
      const sorted = [...prev[serviceKey].bands].sort(
        (a, b) => (a.minSqFt || 0) - (b.minSqFt || 0)
      );
      const lastIdx  = sorted.length - 1;
      const prevLast = sorted[lastIdx];
      // Give the current open-ended band a concrete upper bound
      const prevMax  = parseInt(prevLast.minSqFt, 10) + 9999;
      const updated  = sorted.map((b, i) =>
        i === lastIdx ? { ...b, maxSqFt: prevMax } : b
      );
      return {
        ...prev,
        [serviceKey]: {
          ...prev[serviceKey],
          bands: [
            ...updated,
            { id: `b_${Date.now()}`, minSqFt: prevMax + 1, maxSqFt: null, basePrice: '' },
          ],
        },
      };
    });
  };

  const removeBand = (serviceKey, bandId) => {
    setFormState((prev) => {
      if (prev[serviceKey].bands.length <= 1) return prev;
      const filtered = prev[serviceKey].bands.filter((b) => b.id !== bandId);
      // Re-enforce open-ended invariant on the last remaining band
      const sorted = [...filtered].sort((a, b) => (a.minSqFt || 0) - (b.minSqFt || 0));
      sorted[sorted.length - 1] = { ...sorted[sorted.length - 1], maxSqFt: null };
      return {
        ...prev,
        [serviceKey]: { ...prev[serviceKey], bands: sorted },
      };
    });
  };

  // Flat-rate (pest_control / lawn_care) save helper
  const handleSaveFlatRate = async (serviceKey, setIsSaving) => {
    const cfg = formState[serviceKey];
    const bandsValidation = validateBands(cfg.bands);
    if (!bandsValidation.valid) {
      toast.error(bandsValidation.error);
      return;
    }
    setIsSaving(true);
    try {
      const resp = await updatePricingConfig(serviceKey, {
        bands: cfg.bands.map((b) => ({
          id:        b.id,
          minSqFt:   parseInt(b.minSqFt, 10)  || 0,
          maxSqFt:   (b.maxSqFt === null || b.maxSqFt === '')
                       ? null
                       : (parseInt(b.maxSqFt, 10) || null),
          basePrice: parseFloat(b.basePrice)  || 0,
        })),
        materialCostDefault: parseFloat(cfg.materialCostDefault) || 0,
        marginPct:           parseFloat(cfg.marginPct)           || 0,
        taxRate:             parseFloat(cfg.taxRate)             || 0,
        defaultFrequency:    cfg.defaultFrequency || 'one-time',
      });
      if (resp.success) {
        const label = serviceKey === 'pest_control' ? 'Pest Control' : 'Lawn Care';
        toast.success(`${label} pricing saved`);
      } else {
        toast.error('Failed to save pricing');
      }
    } catch (e) {
      console.error(`Failed to save ${serviceKey} pricing`, e);
      toast.error('Failed to save pricing');
    } finally {
      setIsSaving(false);
    }
  };

  const updateFlatRateField = (serviceKey, field, value) => {
    setFormState((prev) => ({ ...prev, [serviceKey]: { ...prev[serviceKey], [field]: value } }));
  };

  // Save handlers
  const handleSaveSiding = async () => {
    if (hasSidingErrors) return;
    setIsSavingSiding(true);
    try {
      const resp = await updatePricingConfig('siding', formState.siding);
      if (resp.success) {
        toast.success('Siding pricing saved');
      } else {
        toast.error('Failed to save Siding pricing');
      }
    } catch (e) {
      console.error('Failed to save Siding pricing', e);
      toast.error('Failed to save Siding pricing');
    } finally {
      setIsSavingSiding(false);
    }
  };

  const handleSaveGutter = async () => {
    if (hasGutterErrors) return;
    setIsSavingGutter(true);
    try {
      const resp = await updatePricingConfig('gutter', formState.gutter);
      if (resp.success) {
        toast.success('Gutter pricing saved');
      } else {
        toast.error('Failed to save Gutter pricing');
      }
    } catch (e) {
      console.error('Failed to save Gutter pricing', e);
      toast.error('Failed to save Gutter pricing');
    } finally {
      setIsSavingGutter(false);
    }
  };

  const handleSaveDriveway = async () => {
    if (hasDrivewayErrors) return;
    setIsSavingDriveway(true);
    try {
      const resp = await updatePricingConfig('driveway', formState.driveway);
      if (resp.success) {
        toast.success('Driveway pricing saved');
      } else {
        toast.error('Failed to save Driveway pricing');
      }
    } catch (e) {
      console.error('Failed to save Driveway pricing', e);
      toast.error('Failed to save Driveway pricing');
    } finally {
      setIsSavingDriveway(false);
    }
  };

  const handleSaveDeck = async () => {
    if (hasDeckErrors) return;
    setIsSavingDeck(true);
    try {
      const resp = await updatePricingConfig('deck', formState.deck);
      if (resp.success) {
        toast.success('Deck pricing saved');
      } else {
        toast.error('Failed to save Deck pricing');
      }
    } catch (e) {
      console.error('Failed to save Deck pricing', e);
      toast.error('Failed to save Deck pricing');
    } finally {
      setIsSavingDeck(false);
    }
  };

  const handleSaveFence = async () => {
    if (hasFenceErrors) return;
    setIsSavingFence(true);
    try {
      const resp = await updatePricingConfig('fence', formState.fence);
      if (resp.success) {
        toast.success('Fence pricing saved');
      } else {
        toast.error('Failed to save Fence pricing');
      }
    } catch (e) {
      console.error('Failed to save Fence pricing', e);
      toast.error('Failed to save Fence pricing');
    } finally {
      setIsSavingFence(false);
    }
  };
  
  const updateRoofFormState = (updater) => {
    setFormState((prev) => {
      if (!prev.roof) return prev;
      const nextRoof = typeof updater === 'function' ? updater(prev.roof) : updater;
      return { ...prev, roof: nextRoof };
    });
  };

  const updateRoofErrorState = (section, key, message) => {
    setErrors((prev) => ({
      ...prev,
      roof: {
        ...prev.roof,
        [section]: {
          ...(prev.roof?.[section] || {}),
          [key]: message,
        },
      },
    }));
  };

  const updateHouseWashErrorState = (keyPath, message) => {
    setErrors((prev) => {
      const nextHW = { ...prev.houseWash };
      if (keyPath === 'taxRate' || keyPath === 'baseRatePerSqFt' || keyPath === 'minCharge') {
        nextHW[keyPath] = message;
      } else if (keyPath.startsWith('storyMultiplier.')) {
        const k = keyPath.split('.')[1];
        nextHW.storyMultiplier = {
          ...(nextHW.storyMultiplier || {}),
          [k]: message,
        };
      }
      return { ...prev, houseWash: nextHW };
    });
  };

  // ----------------------------
  // Validators
  // ----------------------------
  const validateMaterialRate = (name, value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      updateRoofErrorState('materials', name, 'Must be a number ≥ 0');
    } else {
      updateRoofErrorState('materials', name, '');
    }
  };

  const validatePitchMultiplier = (key, value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) {
      updateRoofErrorState('pitchMultipliers', key, 'Must be a valid number');
    } else {
      updateRoofErrorState('pitchMultipliers', key, '');
    }
  };

  const validateHouseWashBaseRate = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      updateHouseWashErrorState('baseRatePerSqFt', 'Must be a number ≥ 0');
    } else {
      updateHouseWashErrorState('baseRatePerSqFt', '');
    }
  };

  const validateHouseWashMinCharge = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      updateHouseWashErrorState('minCharge', 'Must be a number ≥ 0');
    } else {
      updateHouseWashErrorState('minCharge', '');
    }
  };

  const validateHouseWashStoryMultiplier = (storyKey, value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) {
      updateHouseWashErrorState(`storyMultiplier.${storyKey}`, 'Must be a valid number');
    } else {
      updateHouseWashErrorState(`storyMultiplier.${storyKey}`, '');
    }
  };

  const validateHouseWashTaxRate = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      updateHouseWashErrorState('taxRate', 'Must be a number ≥ 0');
    } else {
      updateHouseWashErrorState('taxRate', '');
    }
  };

  const validateStageAddon = (key, value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      updateRoofErrorState('stageAddons', key, 'Must be a number ≥ 0');
    } else {
      updateRoofErrorState('stageAddons', key, '');
    }
  };

  const validateTwoStoryAdd = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      updateRoofErrorState('storyAdjustments', 'twoStoryAdd', 'Must be ≥ 0');
    } else {
      updateRoofErrorState('storyAdjustments', 'twoStoryAdd', '');
    }
  };

  const handleHouseWashBaseRateChange = (value) => {
    setFormState((prev) => ({
      ...prev,
      houseWash: {
        ...prev.houseWash,
        baseRatePerSqFt: value,
      },
    }));
    validateHouseWashBaseRate(value);
  };

  const handleHouseWashMinChargeChange = (value) => {
    setFormState((prev) => ({
      ...prev,
      houseWash: {
        ...prev.houseWash,
        minCharge: value,
      },
    }));
    validateHouseWashMinCharge(value);
  };

  const handleHouseWashStoryMultiplierChange = (storyKey, value) => {
    setFormState((prev) => ({
      ...prev,
      houseWash: {
        ...prev.houseWash,
        storyMultiplier: {
          ...(prev.houseWash?.storyMultiplier || {}),
          [storyKey]: value,
        },
      },
    }));
    validateHouseWashStoryMultiplier(storyKey, value);
  };

  const handleHouseWashTaxRateChange = (value) => {
    setFormState((prev) => ({
      ...prev,
      houseWash: {
        ...prev.houseWash,
        taxRate: value,
      },
    }));
    validateHouseWashTaxRate(value);
  };

  const handleSaveHouseWash = async () => {
    if (hasHouseWashErrors) return;
    setIsSavingHouseWash(true);
    try {
      const resp = await updatePricingConfig('houseWash', formState.houseWash);
      if (resp.success) {
        toast.success('House Wash pricing saved');
      } else {
        toast.error('Failed to save House Wash pricing');
      }
    } catch (e) {
      console.error('Failed to save House Wash pricing', e);
      toast.error('Failed to save House Wash pricing');
    } finally {
      setIsSavingHouseWash(false);
    }
  };

  const validateDroneMulti = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      updateRoofErrorState('storyAdjustments', 'droneMulti', 'Must be a number ≥ 0');
    } else {
      updateRoofErrorState('storyAdjustments', 'droneMulti', '');
    }
  };

  const validateTaxRate = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      setErrors((prev) => ({ ...prev, roof: { ...prev.roof, taxRate: 'Must be a number ≥ 0' } }));
    } else {
      setErrors((prev) => ({ ...prev, roof: { ...prev.roof, taxRate: '' } }));
    }
  };

  // ----------------------------
  // Handlers
  // ----------------------------
  const handleMaterialChange = (name, value) => {
    updateRoofFormState((prev) => ({
      ...prev,
      materials: {
        ...(prev.materials || {}),
        [name]: {
          ...(prev.materials?.[name] || {}),
          baseRate: value,
        },
      },
    }));
    validateMaterialRate(name, value);
  };

  const handlePitchChange = (key, value) => {
    updateRoofFormState((prev) => ({
      ...prev,
      pitchMultipliers: {
        ...(prev.pitchMultipliers || {}),
        [key]: value,
      },
    }));
    validatePitchMultiplier(key, value);
  };

  const handleStageAddonChange = (key, value) => {
    updateRoofFormState((prev) => ({
      ...prev,
      stageAddons: {
        ...(prev.stageAddons || {}),
        [key]: value,
      },
    }));
    validateStageAddon(key, value);
  };

  const handleTwoStoryAddChange = (value) => {
    updateRoofFormState((prev) => ({
      ...prev,
      storyAdjustments: {
        ...(prev.storyAdjustments || {}),
        twoStoryAdd: value,
      },
    }));
    validateTwoStoryAdd(value);
  };

  const handleDroneMultiChange = (value) => {
    updateRoofFormState((prev) => ({
      ...prev,
      storyAdjustments: {
        ...(prev.storyAdjustments || {}),
        droneMulti: value,
      },
    }));
    validateDroneMulti(value);
  };

  const handleTaxRateChange = (value) => {
    updateRoofFormState((prev) => ({
      ...prev,
      taxRate: value,
    }));
    validateTaxRate(value);
  };

  const handleSave = async () => {
    if (!formState || hasRoofErrors || loadFailed) return;

    setIsSaving(true);
    try {
      const resp = await updatePricingConfig('roof', formState.roof);
      if (!resp || resp.success !== true) {
        throw new Error('Failed to update roof pricing config');
      }

      toast.success('Pricing settings saved');
    } catch (e) {
      console.error('Failed to save pricing settings', e);
      toast.error('Failed to save pricing settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading && !formState.roof) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Calculator className="h-6 w-6 text-primary" />
          <div>
            <h2 className="text-2xl font-bold text-secondary">Calculator Settings</h2>
            <p className="text-sm text-muted-foreground">
              Manage pricing configuration for all services in your calculator.
            </p>
          </div>
        </div>
        <div className="space-y-4">
          <Card className="p-6 space-y-4">
            <Skeleton className="h-5 w-1/3" />
            <Skeleton className="h-4 w-2/3" />
            <div className="grid gap-3 md:grid-cols-3">
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
            </div>
          </Card>
          <Card className="p-6 space-y-4">
            <Skeleton className="h-5 w-1/4" />
            <Skeleton className="h-4 w-1/2" />
            <div className="grid gap-3 md:grid-cols-3">
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Calculator className="h-6 w-6 text-primary" />
        <div>
          <h2 className="text-2xl font-bold text-secondary">Calculator Settings</h2>
          <p className="text-sm text-muted-foreground">
            Manage pricing configuration for roof, house wash, and other services.
          </p>
        </div>
      </div>

      <Card className="p-4 flex items-center justify-between gap-4" data-testid="load-starter-values-card">
        <div>
          <p className="text-sm font-medium text-secondary">New here? Load starter values</p>
          <p className="text-xs text-muted-foreground max-w-md">
            Copies suggested starting prices into your company's settings for any service you
            haven't configured yet. Review and Save each one — tax stays governed by your Billing settings.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleLoadStarterValues}
          disabled={isLoadingStarter}
          data-testid="load-starter-values-btn"
        >
          {isLoadingStarter ? 'Loading…' : 'Load starter values'}
        </Button>
      </Card>

      <Accordion type="multiple" defaultValue={[]} className="space-y-4">
        <AccordionItem value="roof">
          <AccordionTrigger>Roof Pricing Settings</AccordionTrigger>
          <AccordionContent>

      {/* Material Rates */}
      <Card className="p-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold">Material Rates</h3>
          <p className="text-xs text-muted-foreground">
            Base rate per square foot for each roof material.
          </p>
        </div>
        <Separator />
        <div className="grid gap-4 md:grid-cols-3">
          {['asphalt', 'cedar', 'stone'].map((materialKey) => (
            <div key={materialKey} className="space-y-1">
              <Label className="capitalize" htmlFor={`material-${materialKey}`}>
                {materialKey} base rate
              </Label>
              <Input
                id={`material-${materialKey}`}
                type="text" inputMode="decimal"
                min={0}
                step={0.01}
                value={formState.roof?.materials?.[materialKey]?.baseRate ?? ''}
                onChange={(e) => handleMaterialChange(materialKey, e.target.value)}
              />
              {errors.roof.materials?.[materialKey] && (
                <p className="text-xs text-red-600">{errors.roof.materials[materialKey]}</p>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Pitch Multipliers */}
      <Card className="p-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold">Pitch Multipliers</h3>
          <p className="text-xs text-muted-foreground">
            Adjust the effective area based on roof pitch.
          </p>
        </div>
        <Separator />
        <div className="grid gap-3">
          {requiredPitches.map((pitch) => (
            <div key={pitch} className="grid grid-cols-[120px,1fr] items-center gap-3">
              <Label htmlFor={`pitch-${pitch.replace('/', '-')}`}>{pitch}</Label>
              <div className="space-y-1">
                <Input
                  id={`pitch-${pitch.replace('/', '-')}`}
                  type="text" inputMode="decimal"
                  min={0.5}
                  max={5}
                  step={0.0001}
                  value={formState.roof?.pitchMultipliers?.[pitch] ?? ''}
                  onChange={(e) => handlePitchChange(pitch, e.target.value)}
                />
                {errors.roof.pitchMultipliers?.[pitch] && (
                  <p className="text-xs text-red-600">{errors.roof.pitchMultipliers[pitch]}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Stage Add-ons */}
      <Card className="p-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold">Stage Add-ons</h3>
          <p className="text-xs text-muted-foreground">
            Additional rate per square foot based on contamination level.
          </p>
        </div>
        <Separator />
        <div className="grid gap-4 md:grid-cols-3">
          {['stage1', 'stage2', 'stage3'].map((stageKey) => (
            <div key={stageKey} className="space-y-1">
              <Label className="capitalize" htmlFor={`stage-${stageKey}`}>
                {stageKey}
              </Label>
              <Input
                id={`stage-${stageKey}`}
                type="text" inputMode="decimal"
                min={0}
                max={5}
                step={0.01}
                value={formState.roof?.stageAddons?.[stageKey] ?? ''}
                onChange={(e) => handleStageAddonChange(stageKey, e.target.value)}
              />
              {errors.roof.stageAddons?.[stageKey] && (
                <p className="text-xs text-red-600">{errors.roof.stageAddons[stageKey]}</p>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Story Adjustments */}
      <Card className="p-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold">Story Adjustments</h3>
          <p className="text-xs text-muted-foreground">
            Adjustments applied for multi-story roofs.
          </p>
        </div>
        <Separator />
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="twoStoryAdd">Two-story add (flat $)</Label>
            <Input
              id="twoStoryAdd"
              type="text" inputMode="decimal"
              min={0}
              step={1}
              value={formState.roof?.storyAdjustments?.twoStoryAdd ?? ''}
              onChange={(e) => handleTwoStoryAddChange(e.target.value)}
            />
            {errors.roof.storyAdjustments?.twoStoryAdd && (
              <p className="text-xs text-red-600">{errors.roof.storyAdjustments.twoStoryAdd}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="droneMulti">Three-story multiplier</Label>
            <Input
              id="droneMulti"
              type="text" inputMode="decimal"
              min={1}
              max={5}
              step={0.01}
              value={formState.roof?.storyAdjustments?.droneMulti ?? ''}
              onChange={(e) => handleDroneMultiChange(e.target.value)}
            />
            {errors.roof.storyAdjustments?.droneMulti && (
              <p className="text-xs text-red-600">{errors.roof.storyAdjustments.droneMulti}</p>
            )}
          </div>
        </div>
      </Card>

      {/* Tax Rate */}
      <Card className="p-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold">Tax Rate</h3>
          <p className="text-xs text-muted-foreground">
            Default tax rate (%) used for roof pricing calculations.
          </p>
        </div>
        <Separator />
        <div className="space-y-1 max-w-xs">
          <Label htmlFor="taxRate">Tax Rate (%)</Label>
          <Input
            id="taxRate"
            type="text" inputMode="decimal"
            min={0}
            max={15}
            step={0.01}
            value={formState.roof?.taxRate ?? ''}
            onChange={(e) => handleTaxRateChange(e.target.value)}
          />
          {errors.roof.taxRate && <p className="text-xs text-red-600">{errors.roof.taxRate}</p>}
        </div>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end pt-2">
        <Button
          type="button"
          onClick={handleSave}
          disabled={isSaving || hasRoofErrors || loadFailed || !formState}
        >
          {isSaving ? 'Saving…' : 'Save Roof Settings'}
        </Button>
      </div>

          </AccordionContent>
        </AccordionItem>

        {/* House Wash Pricing removed from Settings UI for v1. Backend support remains. */}

        <AccordionItem value="siding">
          <AccordionTrigger>Siding Pricing Settings</AccordionTrigger>
          <AccordionContent>
            <Card className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold">Siding Base Pricing</h3>
                <p className="text-xs text-muted-foreground">
                  Configure base rates and minimum charge for siding cleaning.
                </p>
              </div>
              <Separator />
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold">Materials</h4>
                  <p className="text-xs text-muted-foreground">
                    Configure base rates and minimum charge per material. Story multipliers and tax rate
                    are applied globally for all materials.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {['vinyl', 'hardie', 'wood'].map((materialKey) => {
                    const material = formState.siding.materials?.[materialKey] || { baseRatePerSqFt: '', minCharge: '' };
                    return (
                      <div key={materialKey} className="space-y-2 border rounded-lg p-3 bg-muted/40">
                        <h5 className="text-sm font-semibold capitalize">{materialKey}</h5>
                        <div className="space-y-1">
                          <Label htmlFor={`siding-${materialKey}-base-rate`}>Base Rate per Sq Ft</Label>
                          <Input
                            id={`siding-${materialKey}-base-rate`}
                            type="text" inputMode="decimal"
                            min={0}
                            step={0.01}
                            value={material.baseRatePerSqFt}
                            onChange={(e) => handleSidingMaterialBaseRateChange(materialKey, e.target.value)}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor={`siding-${materialKey}-min-charge`}>Minimum Charge</Label>
                          <Input
                            id={`siding-${materialKey}-min-charge`}
                            type="text" inputMode="decimal"
                            min={0}
                            step={1}
                            value={material.minCharge}
                            onChange={(e) => handleSidingMaterialMinChargeChange(materialKey, e.target.value)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold">Siding Story Multipliers</h3>
                <p className="text-xs text-muted-foreground">
                  Multipliers applied based on number of stories.
                </p>
              </div>
              <Separator />
              <div className="grid gap-4 md:grid-cols-3">
                {['1', '2', '3'].map((storyKey) => (
                  <div key={storyKey} className="space-y-1">
                    <Label htmlFor={`siding-story-${storyKey}`}>{storyKey} Story</Label>
                    <Input
                      id={`siding-story-${storyKey}`}
                      type="text" inputMode="decimal"
                      min={0.5}
                      max={5}
                      step={0.01}
                      value={formState.siding.storyMultiplier?.[storyKey] ?? ''}
                      onChange={(e) => handleSidingStoryMultiplierChange(storyKey, e.target.value)}
                    />
                    {errors.siding.storyMultiplier?.[storyKey] && (
                      <p className="text-xs text-red-600">{errors.siding.storyMultiplier[storyKey]}</p>
                    )}
                  </div>
                ))}
              </div>

              <div>
                <h3 className="text-lg font-semibold">Siding Tax Rate</h3>
                <p className="text-xs text-muted-foreground">
                  Default tax rate (%) used for siding pricing.
                </p>
              </div>
              <Separator />
              <div className="space-y-1 max-w-xs">
                <Label htmlFor="siding-taxRate">Tax Rate (%)</Label>
                <Input
                  id="siding-taxRate"
                  type="text" inputMode="decimal"
                  min={0}
                  max={100}
                  step={0.01}
                  value={formState.siding.taxRate}
                  onChange={(e) => handleSidingTaxRateChange(e.target.value)}
                />
                {errors.siding.taxRate && (
                  <p className="text-xs text-red-600">{errors.siding.taxRate}</p>
                )}
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  type="button"
                  onClick={handleSaveSiding}
                  disabled={isSavingSiding || hasSidingErrors}
                >
                  {isSavingSiding ? 'Saving…' : 'Save Siding Settings'}
                </Button>
              </div>
            </Card>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="gutter">
          <AccordionTrigger>Gutter Cleaning Pricing Settings</AccordionTrigger>
          <AccordionContent>
            <Card className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold">Gutter Cleaning Base Pricing</h3>
                <p className="text-xs text-muted-foreground">
                  Configure base rates, story multipliers, and minimum charge for gutter cleaning.
                </p>
              </div>
              <Separator />
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="gutter-base-rate">Base Rate Per Foot</Label>
                  <Input
                    id="gutter-base-rate"
                    type="text" inputMode="decimal"
                    min={0}
                    step={0.01}
                    value={formState.gutter.baseRatePerFoot}
                    onChange={(e) => handleSimpleServiceBaseRateChange('gutter', 'baseRatePerFoot', e.target.value)}
                  />
                  {errors.gutter.baseRatePerFoot && (
                    <p className="text-xs text-red-600">{errors.gutter.baseRatePerFoot}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="gutter-min-charge">Minimum Charge ($)</Label>
                  <Input
                    id="gutter-min-charge"
                    type="number"
                    min={0}
                    step={1}
                    value={formState.gutter.minCharge}
                    onChange={(e) => handleSimpleServiceMinChargeChange('gutter', e.target.value)}
                  />
                  {errors.gutter.minCharge && (
                    <p className="text-xs text-red-600">{errors.gutter.minCharge}</p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold">Gutter Story Multipliers</h3>
                <p className="text-xs text-muted-foreground">
                  Multipliers applied based on number of stories.
                </p>
              </div>
              <Separator />
              <div className="grid gap-4 md:grid-cols-3">
                {['1', '2', '3'].map((storyKey) => (
                  <div key={storyKey} className="space-y-1">
                    <Label htmlFor={`gutter-story-${storyKey}`}>{storyKey} Story</Label>
                    <Input
                      id={`gutter-story-${storyKey}`}
                      type="text" inputMode="decimal"
                      min={0}
                      step={0.01}
                      value={formState.gutter.storyMultiplier?.[storyKey] ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormState((prev) => ({
                          ...prev,
                          gutter: {
                            ...prev.gutter,
                            storyMultiplier: {
                              ...(prev.gutter?.storyMultiplier || {}),
                              [storyKey]: value,
                            },
                          },
                        }));
                        const num = Number(value);
                        setErrors((prev) => ({
                          ...prev,
                          gutter: {
                            ...prev.gutter,
                            storyMultiplier: {
                              ...(prev.gutter?.storyMultiplier || {}),
                              [storyKey]: !Number.isFinite(num) || num < 0 ? 'Must be a number ≥ 0' : '',
                            },
                          },
                        }));
                      }}
                    />
                    {errors.gutter.storyMultiplier?.[storyKey] && (
                      <p className="text-xs text-red-600">{errors.gutter.storyMultiplier[storyKey]}</p>
                    )}
                  </div>
                ))}
              </div>

              <div>
                <h3 className="text-lg font-semibold">Gutter Tax Rate</h3>
                <p className="text-xs text-muted-foreground">
                  Default tax rate (%) used for gutter cleaning pricing.
                </p>
              </div>
              <Separator />
              <div className="space-y-1 max-w-xs">
                <Label htmlFor="gutter-taxRate">Tax Rate (%)</Label>
                <Input
                  id="gutter-taxRate"
                  type="text" inputMode="decimal"
                  min={0}
                  max={100}
                  step={0.01}
                  value={formState.gutter.taxRate}
                  onChange={(e) => handleSimpleServiceTaxRateChange('gutter', e.target.value)}
                />
                {errors.gutter.taxRate && (
                  <p className="text-xs text-red-600">{errors.gutter.taxRate}</p>
                )}
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  type="button"
                  onClick={handleSaveGutter}
                  disabled={isSavingGutter || hasGutterErrors}
                >
                  {isSavingGutter ? 'Saving…' : 'Save Gutter Settings'}
                </Button>
              </div>
            </Card>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="driveway">
          <AccordionTrigger>Driveway Cleaning Pricing Settings</AccordionTrigger>
          <AccordionContent>
            <Card className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold">Driveway Base Pricing</h3>
                <p className="text-xs text-muted-foreground">
                  Configure base rates and minimum charge for driveway cleaning.
                </p>
              </div>
              <Separator />
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="driveway-base-rate">Base Rate Per Sq Ft</Label>
                  <Input
                    id="driveway-base-rate"
                    type="text" inputMode="decimal"
                    min={0}
                    step={0.01}
                    value={formState.driveway.baseRatePerSqFt}
                    onChange={(e) => handleSimpleServiceBaseRateChange('driveway', 'baseRatePerSqFt', e.target.value)}
                  />
                  {errors.driveway.baseRatePerSqFt && (
                    <p className="text-xs text-red-600">{errors.driveway.baseRatePerSqFt}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="driveway-min-charge">Minimum Charge ($)</Label>
                  <Input
                    id="driveway-min-charge"
                    type="number"
                    min={0}
                    step={1}
                    value={formState.driveway.minCharge}
                    onChange={(e) => handleSimpleServiceMinChargeChange('driveway', e.target.value)}
                  />
                  {errors.driveway.minCharge && (
                    <p className="text-xs text-red-600">{errors.driveway.minCharge}</p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold">Driveway Tax Rate</h3>
                <p className="text-xs text-muted-foreground">
                  Default tax rate (%) used for driveway cleaning pricing.
                </p>
              </div>
              <Separator />
              <div className="space-y-1 max-w-xs">
                <Label htmlFor="driveway-taxRate">Tax Rate (%)</Label>
                <Input
                  id="driveway-taxRate"
                  type="text" inputMode="decimal"
                  min={0}
                  max={100}
                  step={0.01}
                  value={formState.driveway.taxRate}
                  onChange={(e) => handleSimpleServiceTaxRateChange('driveway', e.target.value)}
                />
                {errors.driveway.taxRate && (
                  <p className="text-xs text-red-600">{errors.driveway.taxRate}</p>
                )}
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  type="button"
                  onClick={handleSaveDriveway}
                  disabled={isSavingDriveway || hasDrivewayErrors}
                >
                  {isSavingDriveway ? 'Saving…' : 'Save Driveway Settings'}
                </Button>
              </div>
            </Card>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="deck">
          <AccordionTrigger>Deck Cleaning Pricing Settings</AccordionTrigger>
          <AccordionContent>
            <Card className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold">Deck Base Pricing</h3>
                <p className="text-xs text-muted-foreground">
                  Configure base rates and minimum charge for deck cleaning.
                </p>
              </div>
              <Separator />
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="deck-base-rate-wood">Base Rate Per Sq Ft (Wood)</Label>
                  <Input
                    id="deck-base-rate-wood"
                    type="text" inputMode="decimal"
                    min={0}
                    step={0.01}
                    value={formState.deck.baseRatePerSqFt?.wood ?? ''}
                    onChange={(e) => handleDeckBaseRateChange('wood', e.target.value)}
                  />
                  {errors.deck.baseRatePerSqFt?.wood && (
                    <p className="text-xs text-red-600">{errors.deck.baseRatePerSqFt.wood}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="deck-base-rate-composite">Base Rate Per Sq Ft (Composite)</Label>
                  <Input
                    id="deck-base-rate-composite"
                    type="text" inputMode="decimal"
                    min={0}
                    step={0.01}
                    value={formState.deck.baseRatePerSqFt?.composite ?? ''}
                    onChange={(e) => handleDeckBaseRateChange('composite', e.target.value)}
                  />
                  {errors.deck.baseRatePerSqFt?.composite && (
                    <p className="text-xs text-red-600">{errors.deck.baseRatePerSqFt.composite}</p>
                  )}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2 mt-4">
                <div className="space-y-1">
                  <Label htmlFor="deck-min-charge">Minimum Charge ($)</Label>
                  <Input
                    id="deck-min-charge"
                    type="number"
                    min={0}
                    step={1}
                    value={formState.deck.minCharge}
                    onChange={(e) => handleSimpleServiceMinChargeChange('deck', e.target.value)}
                  />
                  {errors.deck.minCharge && (
                    <p className="text-xs text-red-600">{errors.deck.minCharge}</p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold">Deck Tax Rate</h3>
                <p className="text-xs text-muted-foreground">
                  Default tax rate (%) used for deck cleaning pricing.
                </p>
              </div>
              <Separator />
              <div className="space-y-1 max-w-xs">
                <Label htmlFor="deck-taxRate">Tax Rate (%)</Label>
                <Input
                  id="deck-taxRate"
                  type="text" inputMode="decimal"
                  min={0}
                  max={100}
                  step={0.01}
                  value={formState.deck.taxRate}
                  onChange={(e) => handleSimpleServiceTaxRateChange('deck', e.target.value)}
                />
                {errors.deck.taxRate && (
                  <p className="text-xs text-red-600">{errors.deck.taxRate}</p>
                )}
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  type="button"
                  onClick={handleSaveDeck}
                  disabled={isSavingDeck || hasDeckErrors}
                >
                  {isSavingDeck ? 'Saving…' : 'Save Deck Settings'}
                </Button>
              </div>
            </Card>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="fence">
          <AccordionTrigger>Fence Cleaning Pricing Settings</AccordionTrigger>
          <AccordionContent>
            <Card className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold">Fence Cleaning Base Pricing</h3>
                <p className="text-xs text-muted-foreground">
                  Configure base rates and minimum charge for fence cleaning.
                </p>
              </div>
              <Separator />
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-1">
                  <Label htmlFor="fence-base-rate-4ft">Base Rate Per Foot (4ft)</Label>
                  <Input
                    id="fence-base-rate-4ft"
                    type="text" inputMode="decimal"
                    min={0}
                    step={0.01}
                    value={formState.fence.baseRatePerFoot?.['4ft'] ?? ''}
                    onChange={(e) => handleFenceBaseRateChange('4ft', e.target.value)}
                  />
                  {errors.fence.baseRatePerFoot?.['4ft'] && (
                    <p className="text-xs text-red-600">{errors.fence.baseRatePerFoot['4ft']}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="fence-base-rate-6ft">Base Rate Per Foot (6ft)</Label>
                  <Input
                    id="fence-base-rate-6ft"
                    type="text" inputMode="decimal"
                    min={0}
                    step={0.01}
                    value={formState.fence.baseRatePerFoot?.['6ft'] ?? ''}
                    onChange={(e) => handleFenceBaseRateChange('6ft', e.target.value)}
                  />
                  {errors.fence.baseRatePerFoot?.['6ft'] && (
                    <p className="text-xs text-red-600">{errors.fence.baseRatePerFoot['6ft']}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="fence-base-rate-8ft">Base Rate Per Foot (8ft)</Label>
                  <Input
                    id="fence-base-rate-8ft"
                    type="text" inputMode="decimal"
                    min={0}
                    step={0.01}
                    value={formState.fence.baseRatePerFoot?.['8ft'] ?? ''}
                    onChange={(e) => handleFenceBaseRateChange('8ft', e.target.value)}
                  />
                  {errors.fence.baseRatePerFoot?.['8ft'] && (
                    <p className="text-xs text-red-600">{errors.fence.baseRatePerFoot['8ft']}</p>
                  )}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2 mt-4">
                <div className="space-y-1">
                  <Label htmlFor="fence-min-charge">Minimum Charge ($)</Label>
                  <Input
                    id="fence-min-charge"
                    type="number"
                    min={0}
                    step={1}
                    value={formState.fence.minCharge}
                    onChange={(e) => handleSimpleServiceMinChargeChange('fence', e.target.value)}
                  />
                  {errors.fence.minCharge && (
                    <p className="text-xs text-red-600">{errors.fence.minCharge}</p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold">Fence Tax Rate</h3>
                <p className="text-xs text-muted-foreground">
                  Default tax rate (%) used for fence cleaning pricing.
                </p>
              </div>
              <Separator />
              <div className="space-y-1 max-w-xs">
                <Label htmlFor="fence-taxRate">Tax Rate (%)</Label>
                <Input
                  id="fence-taxRate"
                  type="text" inputMode="decimal"
                  min={0}
                  max={100}
                  step={0.01}
                  value={formState.fence.taxRate}
                  onChange={(e) => handleSimpleServiceTaxRateChange('fence', e.target.value)}
                />
                {errors.fence.taxRate && (
                  <p className="text-xs text-red-600">{errors.fence.taxRate}</p>
                )}
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  type="button"
                  onClick={handleSaveFence}
                  disabled={isSavingFence || hasFenceErrors}
                >
                  {isSavingFence ? 'Saving…' : 'Save Fence Settings'}
                </Button>
              </div>
            </Card>
          </AccordionContent>
        </AccordionItem>

        {/* ── Pest Control Pricing ──────────────────────────────────────────── */}
        {[
          { key: 'pest_control', label: 'Pest Control', isSaving: isSavingPest, setIsSaving: setIsSavingPest },
          { key: 'lawn_care',    label: 'Lawn Care',    isSaving: isSavingLawn, setIsSaving: setIsSavingLawn },
        ].map(({ key, label, isSaving, setIsSaving }) => (
          <AccordionItem key={key} value={key}>
            <AccordionTrigger>{label} Pricing Settings</AccordionTrigger>
            <AccordionContent>
              <Card className="p-6 space-y-6">

                {/* ── Pricing Bands ─────────────────────────────────────── */}
                <div>
                  <h3 className="text-lg font-semibold">Pricing Bands</h3>
                  <p className="text-xs text-muted-foreground">
                    Define price ranges by property sq ft. The calculator will pre-fill the matching band price.
                    Office staff can still override at quote time.
                  </p>
                </div>
                <Separator />

                {/* Column headers */}
                <div className="grid grid-cols-[1fr_1fr_1fr_40px] gap-2 text-xs font-medium text-muted-foreground px-1">
                  <span>From (sq ft)</span>
                  <span>To (sq ft)</span>
                  <span>Price per visit</span>
                  <span />
                </div>

                {/* Band rows */}
                <div className="space-y-2">
                  {(formState[key]?.bands ?? []).map((band, i) => {
                    const isLast = i === (formState[key].bands.length - 1);
                    return (
                      <div key={band.id} className="grid grid-cols-[1fr_1fr_1fr_40px] gap-2 items-center">
                        {/* Min sq ft */}
                        <Input
                          type="number"
                          min="0"
                          value={band.minSqFt}
                          onChange={(e) => updateBandField(key, band.id, 'minSqFt', e.target.value)}
                          className="font-mono text-sm h-9"
                          placeholder="0"
                          data-testid={`band-min-${key}-${i}`}
                        />
                        {/* Max sq ft */}
                        {isLast ? (
                          <Input
                            value="No limit"
                            disabled
                            className="font-mono text-sm h-9 bg-muted text-muted-foreground"
                          />
                        ) : (
                          <Input
                            type="number"
                            min="0"
                            value={band.maxSqFt ?? ''}
                            onChange={(e) => updateBandField(key, band.id, 'maxSqFt', e.target.value)}
                            className="font-mono text-sm h-9"
                            placeholder="—"
                            data-testid={`band-max-${key}-${i}`}
                          />
                        )}
                        {/* Price */}
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                          <Input
                            type="text"
                            inputMode="decimal"
                            placeholder="0.00"
                            value={band.basePrice}
                            onChange={(e) => updateBandField(key, band.id, 'basePrice', e.target.value)}
                            className="pl-7 font-mono text-sm h-9"
                            data-testid={`band-price-${key}-${i}`}
                          />
                        </div>
                        {/* Delete */}
                        <button
                          type="button"
                          className="flex items-center justify-center h-9 w-9 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-30 disabled:pointer-events-none"
                          onClick={() => removeBand(key, band.id)}
                          disabled={formState[key].bands.length <= 1}
                          data-testid={`band-delete-${key}-${i}`}
                          aria-label="Remove band"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                <button
                  type="button"
                  className="w-full rounded-md border border-dashed border-border py-2 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                  onClick={() => addBand(key)}
                  data-testid={`band-add-${key}`}
                >
                  + Add Band
                </button>

                {/* Material Cost + Margin + Tax */}
                <div>
                  <h3 className="text-lg font-semibold">Cost &amp; Margin Defaults</h3>
                  <p className="text-xs text-muted-foreground">
                    Defaults pre-filled in the calculator. All fields are optional and can be overridden per quote.
                  </p>
                </div>
                <Separator />
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-1">
                    <Label htmlFor={`${key}-material-cost`}>Material Cost Default ($)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <Input
                        id={`${key}-material-cost`}
                        type="text"
                        inputMode="decimal"
                        placeholder="0.00"
                        className="pl-7 font-mono"
                        value={formState[key]?.materialCostDefault ?? ''}
                        onChange={(e) => updateFlatRateField(key, 'materialCostDefault', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`${key}-margin`}>Target Margin (%)</Label>
                    <div className="relative">
                      <Input
                        id={`${key}-margin`}
                        type="text"
                        inputMode="decimal"
                        placeholder="30"
                        className="font-mono"
                        value={formState[key]?.marginPct ?? ''}
                        onChange={(e) => updateFlatRateField(key, 'marginPct', e.target.value)}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`${key}-tax`}>Tax Rate (%)</Label>
                    <div className="relative">
                      <Input
                        id={`${key}-tax`}
                        type="text"
                        inputMode="decimal"
                        placeholder="0"
                        className="font-mono"
                        value={formState[key]?.taxRate ?? ''}
                        onChange={(e) => updateFlatRateField(key, 'taxRate', e.target.value)}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                    </div>
                  </div>
                </div>

                {/* Default Frequency — binary: one-time vs recurring program */}
                <div>
                  <h3 className="text-lg font-semibold">Default Frequency</h3>
                  <p className="text-xs text-muted-foreground">
                    Whether the calculator pre-selects this service as a recurring program by default. Operators can always toggle it per quote.
                  </p>
                </div>
                <Separator />
                <div className="max-w-md">
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <Checkbox
                      checked={(formState[key]?.defaultFrequency ?? 'one-time') !== 'one-time'}
                      onCheckedChange={(v) => updateFlatRateField(key, 'defaultFrequency', v === true ? 'recurring' : 'one-time')}
                      className="mt-0.5"
                      data-testid={`default-recurring-${key}`}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium">Default to recurring program</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {((formState[key]?.defaultFrequency ?? 'one-time') !== 'one-time')
                          ? 'Calculator will pre-check “Sell as service program” for this service.'
                          : 'Calculator will default to one-time for this service.'}
                      </div>
                    </div>
                  </label>
                </div>

                <div className="flex justify-end pt-2">
                  <Button
                    type="button"
                    onClick={() => handleSaveFlatRate(key, setIsSaving)}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving…' : `Save ${label} Settings`}
                  </Button>
                </div>
              </Card>
            </AccordionContent>
          </AccordionItem>
        ))}

      </Accordion>
    </div>
  );
};

export default PricingSettings;
